<script lang="ts">
  import { DonationRequestStatusEnum } from "../lib/api/admin/donation.service";

  // Define status types for the badge
  export let status: DonationRequestStatusEnum | string = DonationRequestStatusEnum.PENDING;
  export let showIcon: boolean = true;
  export let customClass: string = "";
  export let tooltip: string | null = null; // Optional tooltip text
  
  // Map status to colors (handles both enum and string)
  const getColorClass = (status: DonationRequestStatusEnum | string): string => {
    const statusStr = typeof status === 'string' ? status.toUpperCase() : status;
    
    // Handle string statuses (like 'Active', 'Pending', 'Declined', 'Approved', 'Unapproved')
    if (typeof status === 'string') {
      const normalized = status.toLowerCase();
      if (normalized === 'active' || normalized === 'approved') return "approved";
      if (normalized === 'pending') return "pending";
      if (normalized === 'declined' || normalized === 'rejected' || normalized === 'unapproved') return "rejected";
      if (normalized === 'completed' || normalized === 'processed' || normalized === 'paid') return "completed";
      if (normalized === 'verified') return "verified";
      if (normalized === 'invited') return "invited";
      if (normalized === 'awaiting employer registration' || normalized.includes('awaiting')) return "awaiting-registration";
    }
    
    // Handle enum statuses
    switch (statusStr) {
      case DonationRequestStatusEnum.APPROVED:
      case 'APPROVED':
        return "approved";
      case DonationRequestStatusEnum.REJECTED:
      case 'REJECTED':
        return "rejected";
      case DonationRequestStatusEnum.COMPLETED:
      case 'COMPLETED':
        return "completed";
      case DonationRequestStatusEnum.PROCESSED:
      case 'PROCESSED':
        return "processed";
      case DonationRequestStatusEnum.VERIFIED:
      case 'VERIFIED':
        return "verified";
      case DonationRequestStatusEnum.AWAITING_EMPLOYER_REGISTRATION:
      case 'AWAITING EMPLOYER REGISTRATION':
        return "awaiting-registration";
      case DonationRequestStatusEnum.PENDING:
      case 'PENDING':
      default:
        return "pending";
    }
  };
  
  // Get tooltip text
  $: tooltipText = tooltip || getStatusTooltip(status);
  
  function getStatusTooltip(status: DonationRequestStatusEnum | string): string {
    const statusStr = typeof status === 'string' ? status : status;
    const normalized = typeof status === 'string' ? status.toLowerCase() : '';
    
    if (normalized === 'approved' || normalized === 'active') return 'Approved and active';
    if (normalized === 'pending') return 'Pending review';
    if (normalized === 'declined' || normalized === 'rejected' || normalized === 'unapproved') return 'Declined or rejected';
    if (normalized === 'completed') return 'Completed successfully';
    if (normalized === 'processed') return 'Processed';
    if (normalized === 'verified') return 'Verified';
    if (normalized === 'invited') return 'Invited – awaiting registration';
    if (normalized === 'awaiting employer registration' || normalized.includes('awaiting')) {
      return 'Awaiting employer registration';
    }
    
    return String(status);
  }

  // Map status to icons (handles both enum and string)
  const getIcon = (status: DonationRequestStatusEnum | string): { svg: string; label: string } => {
    const normalized = typeof status === 'string' ? status.toLowerCase() : '';
    
    // Handle string statuses
    if (typeof status === 'string') {
      if (normalized === 'active' || normalized === 'approved') {
        return {  
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Approved"
        };
      }
      if (normalized === 'declined' || normalized === 'rejected' || normalized === 'unapproved') {
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>`,
          label: "Rejected"
        };
      }
      if (normalized === 'completed' || normalized === 'processed') {
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: normalized === 'completed' ? "Completed" : "Processed"
        };
      }
      if (normalized === 'verified') {
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Verified"
        };
      }
      if (normalized === 'invited') {
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>`,
          label: "Invited"
        };
      }
      if (normalized === 'awaiting employer registration' || normalized.includes('awaiting')) {
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>`,
          label: "Awaiting registration"
        };
      }
    }
    
    // Handle enum statuses
    switch (status) {
      case DonationRequestStatusEnum.APPROVED:
        return {  
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Approved"
        };
      case DonationRequestStatusEnum.COMPLETED:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Completed"
        };
      case DonationRequestStatusEnum.REJECTED:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>`,
          label: "Rejected"
        };
      case DonationRequestStatusEnum.PROCESSED:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Processed"
        };
      case DonationRequestStatusEnum.VERIFIED:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>`,
          label: "Verified"
        };
      case DonationRequestStatusEnum.AWAITING_EMPLOYER_REGISTRATION:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>`,
          label: "Awaiting registration"
        };
      case DonationRequestStatusEnum.PENDING:
      default:
        return {
          svg: `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>`,
          label: "Pending"
        };
    }
  };

  $: colorClass = getColorClass(status);
  $: icon = getIcon(status);
</script>

<div 
  class="status-badge {colorClass} {customClass}" 
  role="status"
  aria-label={tooltipText}
  title={tooltipText}
>
  {#if showIcon}
    <span class="icon" aria-hidden="true">
      {@html icon.svg}
    </span>
  {/if}
  <span class="text">
    <slot>{status}</slot>
  </span>
</div>

<style>
  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    gap: 0.375rem;
    cursor: help;
    position: relative;
  }

  .icon {
    display: flex;
    align-items: center;
  }

  .text {
    margin-top: 1px;
  }

  /* Status-specific styling — colours drawn from the canonical palette
     (TONE_PALETTE in src/lib/donationLifecycle.ts / lc-tone-* in app.css). */
  .pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .pending .icon {
    color: #92400e;
  }

  .approved {
    background-color: #d1fae5 !important;
    color: #065f46;
  }

  .approved .icon {
    color: #065f46;
  }

  .rejected {
    background-color: #fee2e2;
    color: #991b1b;
  }

  .rejected .icon {
    color: #991b1b;
  }

  .verified {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .verified .icon {
    color: #1e40af;
  }

  .completed {
    background-color: #d1fae5;
    color: #065f46;
  }

  .completed .icon {
    color: #065f46;
  }

  .processed {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .processed .icon {
    color: #1e40af;
  }

  .awaiting-registration {
    background-color: #f3f4f6;
    color: #374151;
  }

  .awaiting-registration .icon {
    color: #374151;
  }

  .invited {
    background-color: #eef2ff;
    color: #4338ca;
  }

  .invited .icon {
    color: #4338ca;
  }
</style> 